import React, { useState, useEffect, } from "react";
import { createUseStyles } from "react-jss";
import { BodyText } from "./BodyText";
import { BASE_BUTTON } from "./Button/Button.styles";
import { useLocalStorage } from "./hooks/useLocalStorage";
import { useTranslation } from "./hooks/useTranslation";
import { THEME, TMS_THEME } from "./theming/theming";
import { Mode } from "./types";
import { classNames } from "./utils/classNames";
const DISPLAY_DURATION_IN_MILLISECONDS = 4000;
export const Alert = ({ message, glyph, type = "warning", overPage = false, className, id, onDismiss, dismissButtonType = "simple", dataId, dataTestId, permanentlyDismiss, }) => {
    const [visible, setVisible] = useState(true);
    const classes = useAlertClasses(type);
    const [dismissed, setDismissed] = useLocalStorage(getAlertStorageKey(permanentlyDismiss?.client, permanentlyDismiss?.name), null);
    useEffect(() => {
        if (overPage) {
            setVisible(true);
            const hideOverPageAlert = setTimeout(() => setVisible(false), DISPLAY_DURATION_IN_MILLISECONDS);
            return () => clearTimeout(hideOverPageAlert);
        }
    }, [overPage, message]);
    if (permanentlyDismiss && dismissed)
        return null;
    const handleDismiss = () => {
        setVisible(false);
        onDismiss && onDismiss();
        if (permanentlyDismiss) {
            setDismissed(new Date().toISOString());
        }
    };
    const glyphToDisplay = glyph || getGlyphForAlertType(type);
    const alert = (React.createElement(AlertWrapper, { type: type, className: className, id: id, dataId: dataId, dataTestId: dataTestId, overPage: overPage },
        React.createElement(BodyText, { glyph: glyphToDisplay, message: message, mode: type === "warning" ? Mode.Light : Mode.Dark }),
        (onDismiss || permanentlyDismiss) && (React.createElement(DismissButton, { id: id ? `${id}--close` : undefined, buttonType: dismissButtonType, onClose: () => handleDismiss(), type: type }))));
    if (!visible)
        return null;
    if (overPage)
        return (React.createElement("div", { className: classNames("over-page", classes.overPageWrapper) }, alert));
    return alert;
};
const AlertWrapper = ({ type, children, className, id, dataId, dataTestId, overPage }) => {
    const classes = useAlertClasses(type);
    return (React.createElement("div", { role: "alert", className: classNames(classes.alert, overPage ? classes.overPageAlert : "", "alert", `alert-${type}`, className ?? ""), id: id, "data-testid": dataTestId, "data-id": dataId }, children));
};
const DismissButton = ({ id, onClose, buttonType, type }) => {
    const t = useTranslation();
    const classes = useAlertClasses(type);
    return buttonType === "simple" ? (React.createElement("button", { type: "button", className: classNames("close", classes.close), "data-dismiss": "modal", "aria-label": t("Dismiss"), onClick: onClose, id: id, "data-testid": id, "data-hook": "dismiss-button" },
        React.createElement("span", { "aria-hidden": "true" }, "\u00D7"))) : (React.createElement("span", { className: classes.closeWrapper },
        React.createElement("button", { type: "button", id: id, "data-dismiss": "modal", onClick: onClose, "data-testid": id, "data-hook": "dismiss-button", className: classes.closeButton }, t("Dismiss"))));
};
export const getGlyphForAlertType = (type) => {
    if (type === "success") {
        return "circleCheck";
    }
    if (type === "info") {
        return "infoSign";
    }
    if (type === "warning") {
        return "exclamationSign";
    }
    if (type === "error") {
        return "circleAlert";
    }
    return undefined;
};
export const getAlertStorageKey = (client, name) => `${client}:permanentlyDismissableAlert:${name}:dismissed`;
const useAlertClasses = (type) => {
    let closeButtonColor = "#fff";
    let alert = {
        margin: [0, 0, 16, 0],
        padding: 16,
        border: [1, "solid", "transparent"],
        borderRadius: 4,
        display: "flex",
        backgroundColor: THEME.palette.primary.base.color,
        color: THEME.palette.primary.base.contrast,
        alignItems: "center",
        "& > .body-text": {
            flexGrow: 1,
        },
        "& ul": {
            paddingLeft: 16,
            marginBottom: 0,
        },
    };
    if (type === "error") {
        alert = {
            ...alert,
            backgroundColor: THEME.palette.error.base.color,
            color: THEME.palette.error.base.contrast,
        };
    }
    if (type === "success") {
        alert = {
            ...alert,
            backgroundColor: THEME.palette.success.base.color,
            color: THEME.palette.success.base.contrast,
        };
    }
    if (type === "warning") {
        alert = {
            ...alert,
            backgroundColor: THEME.palette.warning.base.color,
            color: THEME.palette.warning.base.contrast,
        };
        closeButtonColor = THEME.palette.warning.base.contrast;
    }
    const useStyles = createUseStyles({
        alert,
        overPageWrapper: {
            position: "fixed",
            top: 0,
            left: "40%",
            zIndex: 9999,
        },
        overPageAlert: {
            position: "relative",
            minWidth: 200,
            margin: [30, "auto", 0],
        },
        close: {
            color: closeButtonColor,
            textShadow: "unset",
            opacity: 1,
            marginLeft: 10,
            marginTop: -5,
            "&:hover": {
                color: closeButtonColor,
                opacity: 0.7,
            },
        },
        closeButton: {
            backgroundColor: "transparent",
            border: 0,
            color: closeButtonColor,
            padding: [5, 10],
            ...BASE_BUTTON,
            "&:hover": {
                color: closeButtonColor,
                opacity: 0.8,
            },
        },
        closeWrapper: {
            display: "flex",
            borderLeft: [
                type === "warning" ? TMS_THEME.palette.gray.slate.color : "#fff",
                1,
                "solid",
            ],
            paddingLeft: 15,
        },
    });
    return useStyles();
};
